<template>
  <TypeformQuestion :question="hideResume ? 'Add Required Info 📄' : 'Create a Resume 📄'"
                    max-width-class="max-w-screen-xl"
                    center
                    @prevStep="() => $emit('prevStep')"
                    @nextStep="() => $emit('nextStep')">

    <button v-if="!hideResume" type="button" @click="$emit('toggleUseBdResume')"
            class="font-semibold px-6 text-sm py-3 border mx-auto block rounded-lg opacity-60 hover:opacity-100 transition-all"
            :style="buttonOutline">
      Upload your resume instead →
    </button>

    <div class="text-left bg-white p-4 lg:p-6 rounded-md border border-slate-200">
      <NonResumeCompleteProfile
        :prefilled-education-complete="prefilledEducationComplete"
        :prefilled-career-passport-complete="prefilledCareerPassportComplete"
        :prefilled-exceptional-ability-complete="prefilledExceptionalAbilityComplete"
        :use-store="() => useCandidateApplicationFormStore()"/>
    </div>

  </TypeformQuestion>
</template>

<script setup>
import NonResumeCompleteProfile from "./NonResumeCompleteProfile.vue";
import TypeformQuestion from "./TypeformQuestion.vue";
import {useCandidateApplicationFormStore} from "../stores/CandidateApplicationFormStore.js";
import {computed, inject, onBeforeMount} from "vue";
import {careerPassportComplete, educationComplete, exceptionalAbilityComplete} from "../helpers/CandidateFunctions.js";

const emits = defineEmits(["nextStep", "prevStep", "toggleUseBdResume", "update:validating", "update:validator"]);
defineProps({
  hideResume: Boolean,
});

const store = useCandidateApplicationFormStore();

const typeformData = inject("typeformData", {});
const buttonOutline = computed(() => `border-color: ${typeformData.primaryForeground};color:${typeformData.primaryForeground};`);

const prefilledEducationComplete = computed(() => {
  if (!store.applicationForm.candidate?.education?.length) return false;
  const firstEducation = store.applicationForm.candidate.education[0];
  return educationComplete(firstEducation);
});

const prefilledCareerPassportComplete = computed(() => {
  if (!store.applicationForm.candidate?.careerPassport?.length) return false;
  const firstCareerPassport = store.applicationForm.candidate.careerPassport[0];
  return careerPassportComplete(firstCareerPassport);
});

const prefilledExceptionalAbilityComplete = computed(() => {
  return exceptionalAbilityComplete(store.applicationForm.candidate?.exceptionalAbility);
});

const getUserFilledEducationComplete =() => {
  if (!store.state.education?.length) return false;
  const firstEducation = store.state.education[0];
  return educationComplete(firstEducation);
};

const getUserFilledCareerPassportComplete = () => {
  if (!store.state.careerPassport?.length) return false;
  const firstCareerPassport = store.state.careerPassport[0];
  return careerPassportComplete(firstCareerPassport);
};

const getUserFilledExceptionalAbilityComplete =() => {
  return exceptionalAbilityComplete(store.state.exceptionalAbility);
};

const handleAlreadyCompleted = () => {
  if (
    (prefilledCareerPassportComplete.value || getUserFilledCareerPassportComplete()) &&
    (prefilledEducationComplete.value || getUserFilledEducationComplete()) &&
    (prefilledExceptionalAbilityComplete.value || getUserFilledExceptionalAbilityComplete())
  ) {
    emits("nextStep");
  }
};

onBeforeMount(() => {
  store.setFurthestStep(1, "Create a Resume");

  emits("update:validating", false);
  emits("update:validator", async () => {
    if (!prefilledCareerPassportComplete.value && !getUserFilledCareerPassportComplete()) {
      toast.warning("Please add at least one entry to your work history.");
      return false;
    }
    if (!prefilledEducationComplete.value && !getUserFilledEducationComplete()) {
      toast.warning("Please add at least one education entry.");
      return false;
    }

    if (!prefilledExceptionalAbilityComplete.value && !getUserFilledExceptionalAbilityComplete()) {
      toast.warning("Please add three short exceptional abilities.");
      return false;
    }

    return true;
  });

  handleAlreadyCompleted(); // Must come AFTER setting validator
});
</script>
